<template>
    <div>
        <q-table
            :title="$t('transfers.cm_withdrawals')"
            ref="table"
            :data="displayableData"
            :columns="columns"
            :filter="filter"
            row-key="transfer_reference"
            :pagination.sync="serverPagination"
            :visible-columns="visibleColumns"
            :loading="loading"
            :selection="selection"
            :selected.sync="selectedRows"
            style="margin-bottom: 80px;"
            @request="all"
        >
            <template v-slot:top-left="props">
                <div class="text-h6">
                    CM Balance:
                    <q-skeleton type="QBadge" v-if="balance_loading" class="inline-block" style="width:100px"/>
                    <currency-format
                        class="inline-block text-bold"
                        :value="real_balance"
                        :unit="currency_acronym"
                        decimal-max-length="2"
                        v-else
                    />&nbsp;
                    <q-btn outline round icon="fa fa-refresh" size="xs" color="primary" :loading="balance_loading" @click="syncBalance"/>
                </div>
            </template>

            <template v-slot:top-right="props">
                <q-btn-dropdown
                    outline
                    color="primary"
                    label="View Options"
                    class="q-mr-sm"
                >
                    <div class="row q-col-gutter-md q-pa-md" style="width: 280px">
                        <div class="col col-12">
                            <q-checkbox v-model="show_only_paid_orders" label="Show only Paid orders" color="primary" class="q-ml-xs" />
                        </div>
                    </div>
                </q-btn-dropdown>
                <q-btn label="Merge records" icon="far fa-object-group" color="primary" class="q-mr-sm" v-if="is_selected_rows_matched" @click="mergeAndWithdraw" :loading="!!withdraw_loading"/>
                <q-select
                    dense
                    emit-value
                    map-options
                    option-value="acronym"
                    option-label="name"
                    v-model="currency_acronym"
                    :options="supported_currencies"
                    class="q-mr-sm"
                    label="Currency"
                    @input="refreshPage"
                />
                <q-btn outline :label="$t('refresh')" icon="fas fa-sync" color="primary" class="q-mr-sm" @click="all({pagination: serverPagination, filter})" :loading="loading">
                    <template v-slot:loading>
                        <q-spinner-dots />
                    </template>
                </q-btn>
                <q-input dense debounce="300" v-model="filter" :placeholder="$t('search')" class="q-mr-sm">
                    <template v-slot:prepend>
                        <q-icon name="search" />
                    </template>
                </q-input>
                <q-select
                    v-model="visibleColumns"
                    multiple
                    dense
                    options-dense
                    :display-value="$q.lang.table.columns"
                    emit-value
                    map-options
                    :options="columns"
                    option-value="name"
                    color="primary"
                    style="min-width: 150px;margin-right:10px"
                />
            </template>
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th auto-width />
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                    >
                        <span>{{ col.label }}</span>
                    </q-th>
                </q-tr>
            </template>
            <!-- START CUSTOM COLUMNS -->
            <template v-slot:body="props">
                <q-tr :props="props" :statement="props.row.currentStatus = statuses.find(item => item.name === props.row.status)">
                    <q-td auto-width>
                        <q-checkbox v-model="selectedRows" :val="props.row" />
                    </q-td>
                    <q-td key="transfer_reference" :props="props">
                        <div>{{props.row.transfer_reference}}</div>
                        <div>
                            <q-chip square
                                    :color="props.row.currentStatus.color"
                                    :icon="props.row.currentStatus.icon"
                                    text-color="white"
                                    size="sm"
                            >{{props.row.currentStatus.label}}</q-chip>
                        </div>
                    </q-td>
                    <q-td key="source_currency" :props="props">
                        {{props.row.source_currency}}
                    </q-td>
                    <q-td key="to_currency" :props="props">
                        {{props.row.to_currency}}
                    </q-td>
                    <q-td key="amount" :props="props">
                        <currency-format
                            class="inline-block text-bold"
                            :value="props.row.amount"
                            :unit="props.row.to_currency"
                            decimal-max-length="2"
                        />
                        <q-popup-edit v-model="props.row.amount" buttons>
                            <q-input v-model="props.row.amount" dense autofocus type="number"/>
                        </q-popup-edit>
                    </q-td>
                    <q-td key="order_payments" :props="props">
                        <div>
                            <currency-format
                                class="text-primary"
                                :value="props.row.total_amount"
                                :unit="props.row.to_currency"
                                decimal-max-length="2"
                            />
                            <q-tooltip content-class="bg-amber-1 text-black shadow-4" :offset="[0, 0]">
                                Total amount
                            </q-tooltip>
                        </div>

                        <div>
                            <currency-format
                                class="text-red"
                                :value="props.row.total_paid_out"
                                :unit="props.row.to_currency"
                                decimal-max-length="2"
                            />
                            <q-tooltip content-class="bg-amber-1 text-black shadow-4" :offset="[0, 0]">
                                Total paid out
                            </q-tooltip>
                        </div>
                    </q-td>
                    <q-td key="pre_assigned_code" :props="props">
                        {{props.row.pre_assigned_code}}
                    </q-td>
                    <q-td key="reference" :props="props">
                        {{props.row.reference}}
                        <q-popup-edit v-model="props.row.reference" buttons v-slot="scope">
                            <q-input v-model="scope.value" dense autofocus />
                        </q-popup-edit>
                    </q-td>
                    <q-td key="sent_payment_to_country" :props="props">
                        {{props.row.sent_payment_to_country}}
                    </q-td>
                    <q-td key="bank_name" :props="props">
                        {{props.row.bank_name}}
                    </q-td>
                    <q-td key="bank_swift_bic" :props="props">
                        {{props.row.bank_swift_bic}}
                        <q-popup-edit v-model="props.row.bank_swift_bic" buttons v-slot="scope">
                            <q-input v-model="scope.value" dense autofocus />
                        </q-popup-edit>
                    </q-td>
                    <q-td key="to_account_number" :props="props">
                        {{props.row.to_account_number}}
                        <q-popup-edit v-model="props.row.to_account_number" buttons v-slot="scope">
                            <q-input v-model="scope.value" dense autofocus mask="##-####-#######-###"/>
                        </q-popup-edit>
                    </q-td>
                    <q-td key="bank_account_name" :props="props">
                        {{props.row.bank_account_name}}
                        <q-popup-edit v-model="props.row.bank_account_name" buttons v-slot="scope">
                            <q-input v-model="scope.value" dense autofocus/>
                        </q-popup-edit>
                    </q-td>
                    <q-td key="bank_owner_address_1" :props="props">
                        {{props.row.bank_owner_address_1}}
                        <q-popup-edit v-model="props.row.bank_owner_address_1" buttons v-slot="scope">
                            <q-input v-model="scope.value" dense autofocus/>
                        </q-popup-edit>
                    </q-td>
                    <q-td key="bank_owner_address_2" :props="props">
                        {{props.row.bank_owner_address_2}}
                        <q-popup-edit v-model="props.row.bank_owner_address_2" buttons v-slot="scope">
                            <q-input v-model="scope.value" dense autofocus/>
                        </q-popup-edit>
                    </q-td>
                    <q-td key="bank_owner_address_country" :props="props">
                        {{props.row.bank_owner_address_country}}
                    </q-td>
                    <q-td key="withdraw" :props="props">
                        <q-btn outline dense no-caps
                               :label="$t('transfers.copy_txid')"
                               v-clipboard="props.row.txid"
                               @success="copied('TXID is copied!')"
                               color="info"
                               icon="far fa-copy"
                               v-if="props.row.txid"
                        />
                        <q-btn outline dense no-caps
                               :label="$t('transfers.withdraw')"
                               color="primary"
                               icon="fa-solid fa-arrow-up-from-bracket"
                               @click="withdrawConfirmation(props.row)"
                               :loading="withdraw_loading === props.row.transfer_reference"
                               :disabled="props.row.amount > real_balance"
                               v-else
                        >
                            <q-badge rounded class="q-ml-sm inline-block" color="green" v-if="props.row.transactions.accepted_outgoing_count">
                                {{props.row.transactions.accepted_outgoing_count}}
                                <q-tooltip content-class="bg-amber-1 text-black shadow-4" :offset="[10, 10]">
                                    Accepted outgoing transaction count
                                </q-tooltip>
                            </q-badge>&nbsp;
                            <q-badge rounded class="q-ml-sm inline-block" color="grey" v-if="props.row.transactions.non_accepted_outgoing_count">
                                {{props.row.transactions.non_accepted_outgoing_count}}
                                <q-tooltip content-class="bg-amber-1 text-black shadow-4" :offset="[10, 10]">
                                    Non-accepted outgoing transaction count
                                </q-tooltip>
                            </q-badge>
                        </q-btn>
                    </q-td>
                </q-tr>
            </template>
            <!-- END CUSTOM COLUMNS -->

            <template v-slot:bottom-row="props">
                <tr class="text-green text-bold">
                    <td class="total-row-borders" colspan="2">{{$t('total')}}</td>
                    <td class="total-row-borders text-right">
                        <currency-format
                            class="inline-block"
                            :value="totalAmountOfSelectedRows"
                            :decimal-max-length="2"
                        />
                    </td>
                    <td class="total-row-borders" colspan="12"></td>
                </tr>
            </template>
        </q-table>
        <merged-withdrawals-dialog
            v-model="displayMergedWithdrawalsDialog"
            @submit="withdraw"
            :withdrawal-obj="mergedPayload"
            v-if="mergedPayload"
            :key="Math.random()"
        />

        <confirmation-modal
            v-model="displayConfirmationModal"
            @cancel="readyToWithdraw = null"
            @confirm="withdraw(readyToWithdraw)"
            okBtnLabel="Withdraw"
            v-if="readyToWithdraw"
        >
            Are you sure you want to withdraw <currency-format
            class="inline-block text-bold"
            :value="readyToWithdraw.amount"
            unit="NZD"
            decimal-max-length="2"
        />?
        </confirmation-modal>
    </div>
</template>
<script>

import Transfer from '../../models/Transfer'
import MergedWithdrawalsDialog from "./MergedWithdrawalsDialog"
import ConfirmationModal from "src/components/ConfirmationModal.vue"
import RebitAccount from "Modules/RebitAccount/resources/models/RebitAccount"
import currency from "../../../../Currency/resources/models/Currency.js";

export default {
    name: 'CrownMoneyWithdrawalTable',
    props: {},
    computed: {
        currency() {
            return currency
        },
        columns() {
            return [
                {name: 'transfer_reference', required: true, label: this.$t('transfers.withdrawal_table_titles.transfer_reference'), align: 'center', field: 'transfer_reference', sortable: false},
                {name: 'source_currency', label: this.$t('transfers.withdrawal_table_titles.from_currency'), align: 'left', field: 'source_currency', sortable: true},
                {name: 'to_currency', label: this.$t('transfers.withdrawal_table_titles.to_currency'), align: 'left', field: 'to_currency', sortable: true},
                {name: 'amount', label: this.$t('transfers.withdrawal_table_titles.outstanding_amount'), align: 'right', field: 'amount', sortable: false},
                {name: 'order_payments', label: this.$t('transfers.withdrawal_table_titles.order_payments'), align: 'right', field: 'order_payments', sortable: false},
                {name: 'pre_assigned_code', label: this.$t('transfers.withdrawal_table_titles.pre_assigned_code'), align: 'left', field: 'pre_assigned_code', sortable: false},
                {name: 'reference', label: this.$t('transfers.withdrawal_table_titles.reference'), align: 'left', field: 'reference', sortable: true},
                {name: 'sent_payment_to_country', label: this.$t('transfers.withdrawal_table_titles.sent_payment_to_country'), align: 'left', field: 'sent_payment_to_country', sortable: true},
                {name: 'bank_name', label: this.$t('transfers.withdrawal_table_titles.bank_name'), align: 'left', field: 'bank_name', sortable: false},
                {name: 'bank_swift_bic', label: this.$t('transfers.withdrawal_table_titles.swift_bic'), align: 'left', field: 'bank_swift_bic', sortable: false},
                {name: 'to_account_number', label: this.$t('transfers.withdrawal_table_titles.to_account_number'), align: 'left', field: 'to_account_number', sortable: false},
                {name: 'bank_account_name', label: this.$t('transfers.withdrawal_table_titles.bank_account_name'), align: 'left', field: 'bank_account_name', sortable: false},
                {name: 'bank_owner_address_1', label: this.$t('transfers.withdrawal_table_titles.bank_owner_address_1'), align: 'left', field: 'bank_owner_address_1', sortable: false},
                {name: 'bank_owner_address_2', label: this.$t('transfers.withdrawal_table_titles.bank_owner_address_2'), align: 'left', field: 'bank_owner_address_2', sortable: false},
                {name: 'bank_owner_address_country', label: this.$t('transfers.withdrawal_table_titles.bank_owner_address_country'), align: 'left', field: 'bank_owner_address_country', sortable: false},
                {name: 'withdraw', label: this.$t('transfers.withdraw'), align: 'center', field: 'withdraw', sortable: false}
            ]
        },
        displayableData () {
            return this.show_only_paid_orders ? this.serverData.filter(record => record.status === 'paid' && record.amount > 0) : this.serverData
        },
        is_selected_rows_matched() {
            if (this.selectedRows.length > 1) {
                return this.selectedRows.every((row) => {
                    return (row.pre_assigned_code === this.selectedRows[0].pre_assigned_code) &&
                        (row.to_account_number === this.selectedRows[0].to_account_number)
                })
            }
            return false
        },
        totalAmountOfSelectedRows() {
            return this.selectedRows.reduce((sum, item) => sum + Number(item.amount), 0).roundToX(2)
        }
    },
    data: () => ({
        separator: 'horizontal',
        filter: '',
        loading: false,
        serverPagination: {
            page: 1,
            sortBy: 'created_at',
            rowsPerPage: 15,
            descending: false,
            rowsNumber: 10 // specifying this determines pagination is server-side
        },
        visibleColumns: [
            'transfer_reference', 'amount', 'order_payments', 'pre_assigned_code', 'reference', 'bank_name',
            'to_account_number', 'bank_account_name','bank_owner_address_1','bank_owner_address_2', 'withdraw'
        ],
        statuses: [
            {name:'awaiting', label: 'Awaiting approval', icon: 'fa fa-hourglass-half', color: 'warning'},
            {name:'payment_issue', label: 'Payment issue', icon: 'fa fa-download', color: 'pink'},
            {name:'paid', label: 'Paid', icon: 'fa fa-file-invoice-dollar', color: 'blue'},
            {name:'transfer_issue', label: 'Transfer issue', icon: 'fa fa-upload', color: 'pink'},
            {name:'transferred', label: 'Done', icon: 'fa fa-check-circle', color: 'positive'},
            {name:'canceled', label: 'Cancelled', icon: 'fa fa-window-close', color: 'negative'},
        ],
        supported_currencies: [
            {acronym: 'AUD', name: 'Australian Dollar'},
            {acronym: 'NZD', name: 'New Zealand Dollar'}
        ],
        currency_acronym: 'NZD',
        serverData: [],
        selection: 'none',
        selectedRows: [],
        real_balance: 0,
        readyToWithdraw: null,
        withdraw_loading: null,
        balance_loading: false,
        displayMergedWithdrawalsDialog: false,
        mergedPayload: null,
        show_only_paid_orders: true,
        displayConfirmationModal: false,
    }),
    methods: {
        all({pagination, filter}) {
            this.loading = true
            Transfer.readyOrdersForWithdrawal(`sort=${pagination.sortBy}%7C${pagination.descending ? 'desc' : 'asc'}&page=${pagination.page}&per_page=${pagination.rowsPerPage}&filter=${filter}&currency=${this.currency_acronym}`, ({data, meta}) => {
                this.serverPagination = pagination
                // we also set (or update) rowsNumber
                this.serverPagination.rowsNumber = meta.total
                this.serverData = data
                this.loading = false
            }).catch(error => {
                this.$q.notify({
                    message: error.data.error,
                    color: 'negative',
                    icon: 'fa fa-exclamation-triangle'
                })
                this.loading = false
            })
        },
        withdraw(payload) {
            this.readyToWithdraw = null
            this.withdraw_loading = payload.transfer_reference
            payload.transfer_references = payload.transfer_references || [payload.transfer_reference]
            new Transfer(payload).cmWithdraw(({txid}) => {
                this.serverData.filter(row => payload.transfer_references.includes(row.transfer_reference)).forEach(row => {
                    row.txid = txid
                })
                this.withdraw_loading = null
                this.syncBalance()
            }).catch(error => {
                if (error.status === 412) {
                    this.$q.notify({
                        type: 'negative',
                        message: error.data.error
                    })
                } else if (error.status === 422) {
                    this.errorMessages = []
                    for (let err in error.data.errors) {
                        this.errorMessages.push(error.data.errors[err][0])
                    }
                    this.$q.notify({
                        type: 'negative',
                        message: this.errorMessages[0],
                    })
                }
                this.withdraw_loading = null
            })
        },
        withdrawConfirmation(payload) {
            this.readyToWithdraw = payload
            this.displayConfirmationModal = true
        },
        mergeAndWithdraw() {
            this.displayMergedWithdrawalsDialog = true
            this.mergedPayload = Object.assign({}, this.selectedRows[0])
            this.mergedPayload.amount = this.totalAmountOfSelectedRows
            this.mergedPayload.transfer_references = this.selectedRows.map(item => {
                return item.transfer_reference
            })
        },
        copied(message) {
            this.$q.notify({
                message: message,
                position: 'top'
            })
        },
        syncBalance() {
            this.balance_loading = true
            new RebitAccount({
                currency_acronym: this.currency_acronym
            }).syncRealBalance((balance) => {
                this.balance_loading = false
                this.real_balance = balance
            }).catch((error) => {
                this.balance_loading = false
                switch (error.status) {
                    case 412:
                        this.$q.notify({
                            message: error.data.error,
                            color: 'negative',
                            icon: 'fa fa-exclamation-triangle',
                            timeout: 5000,
                        })
                        this.real_balance = 0
                        break
                    default:
                        this.$q.notify({
                            message: 'An error has occurred!',
                            color: 'negative',
                            icon: 'fa fa-exclamation-triangle'
                        })
                }
            })
        },
        refreshPage() {
            this.syncBalance()
            this.all({
                pagination: this.serverPagination,
                filter: this.filter
            })
        }
    },
    mounted() {
        this.refreshPage()
    },
    components: {
        MergedWithdrawalsDialog,
        ConfirmationModal
    }
}
</script>
